import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  IconButton,
  PagePickerDots,
  SkeletonSpinnerCentered,
} from '@civilgrid/ui/components';
import type React from 'react';
import { useEffect, useState } from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';

type FeatureAnnouncementModalProps = {
  announcementId?: string;
  title?: string;
  isLoading?: boolean;
  pages: { key: string; content: React.ReactNode }[];
  onDismiss?: () => void;
  /** Keep all page nodes mounted to avoid remount/decode churn while paging. */
  keepPagesMounted?: boolean;
  /** When provided, the modal is fully controlled externally and localStorage is not used. */
  open?: boolean;
  onClose?: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  /** Prefix for `data-testid` values on the dialog (panels, footer, nav). Page content sets its own ids. */
  dataTestIdPrefix?: string;
};

export function FeatureAnnouncementModal({
  announcementId,
  title,
  isLoading,
  pages,
  onDismiss,
  keepPagesMounted = false,
  open: controlledOpen,
  onClose,
  onCloseAutoFocus,
  dataTestIdPrefix = 'feature-announcement-modal',
}: FeatureAnnouncementModalProps) {
  const isControlled = controlledOpen !== undefined;
  const tid = (suffix: string) => `${dataTestIdPrefix}-${suffix}`;

  const [autoOpen, setAutoOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [storageKey, setStorageKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof announcementId === 'undefined') {
      setStorageKey(undefined);
    } else {
      setStorageKey(`feature-announcement-${announcementId}`);
    }
  }, [announcementId]);

  useEffect(() => {
    if (isControlled) return;
    if (typeof storageKey === 'undefined' || localStorage.getItem(storageKey)) return;

    const timer = setTimeout(() => {
      setAutoOpen(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [storageKey, isControlled]);

  const isOpen = isControlled ? controlledOpen : autoOpen;

  useEffect(() => {
    if (!isOpen) return;
    capturePosthogEvent(POSTHOG_EVENTS.productUpdateViewed, {
      announcement_id: announcementId,
      source: isControlled ? 'help_menu' : 'auto',
    });
  }, [isOpen, isControlled, announcementId]);

  const handleClose = () => {
    capturePosthogEvent(POSTHOG_EVENTS.productUpdateDismissed, {
      announcement_id: announcementId,
      source: isControlled ? 'help_menu' : 'auto',
    });
    if (isControlled) {
      onClose?.();
    } else {
      setAutoOpen(false);
      if (storageKey) localStorage.setItem(storageKey, 'announcement');
    }
    setCurrentPage(0);
    onDismiss?.();
  };

  if (!isOpen) return null;

  let content: React.ReactNode;
  const totalPages = pages.length;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === Math.max(totalPages - 1, 0);
  if (totalPages === 0) {
    content = <div data-testid={tid('no-new-updates')}>No new updates!</div>;
  } else {
    content = keepPagesMounted
      ? pages.map(({ key, content }, index) => (
          <div
            key={key}
            className={index === currentPage ? 'block' : 'hidden'}
            aria-hidden={index !== currentPage}
            data-testid={tid(`page-panel-${index + 1}`)}
          >
            {content}
          </div>
        ))
      : pages[currentPage].content;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        data-testid={dataTestIdPrefix}
        className={`${totalPages > 0 ? 'max-w-275' : 'max-w-sm'} transition-none w-[90vw] max-h-[90vh] z-100`}
        overlayClassName="z-100"
        title={title ?? 'Product Updates'}
        headerProps={{ className: 'px-6 pt-8 pb-2', 'data-testid': tid('header') }}
        titleProps={{ 'data-testid': tid('title') }}
        closeButtonProps={{ 'data-testid': tid('close') }}
        onInteractOutside={(e) => e.preventDefault()}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogBody className="px-6 py-2.5" data-testid={tid('body')}>
          {isLoading ? <SkeletonSpinnerCentered /> : content}
        </DialogBody>

        <DialogFooter className="justify-end px-6 pb-8 pt-2" data-testid={tid('footer')}>
          {totalPages > 1 && (
            <PagePickerDots
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              data-testid={tid('page-picker')}
              dotDataTestId={(idx) => tid(`page-dot-${idx + 1}`)}
            />
          )}

          <div className="flex items-center gap-2" data-testid={tid('nav-buttons')}>
            {totalPages > 1 && (
              <IconButton
                icon="arrowLeft"
                iconSize={14}
                variant="secondary"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={isFirstPage}
                data-testid={tid('back')}
              >
                Back
              </IconButton>
            )}
            <IconButton
              icon="arrowRight"
              iconSize={14}
              variant="secondary"
              size="small"
              onClick={() => {
                if (isLastPage) {
                  handleClose();
                } else {
                  setCurrentPage((p) => p + 1);
                }
              }}
              data-testid={isLastPage ? tid('done') : tid('next')}
            >
              {isLastPage ? 'Done' : 'Next'}
            </IconButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
